import type { IntegrationDataModelSyncSettings } from '@commenda/api';
import type {
  CreateRootFiInviteLinkSyncConfigItemV4,
  RootFiSyncConfigItemV4,
} from '@commenda/types/src/root-fi/types';
import {
  INTEGRATION_CATEGORY,
  IntegrationType,
  RootfiIntegrationCategory,
} from '@commenda/types/src/root-fi/types';
import type { RootFiIntegration } from '@prisma/client';

import { formatRootFiDataModelName } from './formatRootFiDataModelName';

const ROOT_FI_DEFAULT_SYNC_FROM = '2022-01-01';

/** Category for a platform integration (e.g. QuickBooks → ACCOUNTING). */
export function rootFiCategoryForIntegration(
  integration: RootFiIntegration
): RootfiIntegrationCategory {
  return (
    INTEGRATION_CATEGORY[integration as IntegrationType] ??
    RootfiIntegrationCategory.ACCOUNTING
  );
}

function rootFiFilterSyncConfigByCategory(
  configs: RootFiSyncConfigItemV4[] | null | undefined,
  integrationCategory: RootfiIntegrationCategory
): RootFiSyncConfigItemV4[] {
  return (configs ?? []).filter((c) => c.category === integrationCategory);
}

/** Default rows for an integration, with the sync-from RootFi would otherwise apply made explicit. */
export function rootFiDefaultSyncConfigRows(
  configs: RootFiSyncConfigItemV4[] | null | undefined,
  integrationCategory: RootfiIntegrationCategory
): RootFiSyncConfigItemV4[] {
  return rootFiFilterSyncConfigByCategory(configs, integrationCategory).map(
    (item) => ({
      ...item,
      interval: item.interval ?? 1,
      syncFrom: item.scopeAccess.READ
        ? (item.syncFrom ?? ROOT_FI_DEFAULT_SYNC_FROM).slice(0, 10)
        : undefined,
    })
  );
}

/** The models Commenda will read, in the shape the Sync configuration fields edit. */
export function rootFiSyncConfigRowsToSyncSettings(
  rows: RootFiSyncConfigItemV4[]
): IntegrationDataModelSyncSettings[] {
  return rows
    .filter((row) => row.enabled && row.scopeAccess.READ)
    .map((row) => ({
      dataModel: row.dataModel,
      label: formatRootFiDataModelName(row.dataModel),
      frequency: row.frequency,
      interval: row.interval ?? 1,
      syncFrom: row.syncFrom ?? null,
    }));
}

/** Writes edited frequency / sync-from back onto the matching rows; other rows are untouched. */
export function rootFiApplySyncSettingsToRows(
  rows: RootFiSyncConfigItemV4[],
  settings: IntegrationDataModelSyncSettings[]
): RootFiSyncConfigItemV4[] {
  return rows.map((row) => {
    const edited = settings.find((s) => s.dataModel === row.dataModel);
    if (!edited) return row;
    return {
      ...row,
      frequency: edited.frequency,
      interval: edited.interval,
      syncFrom: edited.syncFrom ?? undefined,
    };
  });
}

/** POST `syncConfig` rows use snake_case in the Commenda API contract. */
export function rootFiSyncConfigItemsToInviteLinkPayload(
  rows: RootFiSyncConfigItemV4[]
): CreateRootFiInviteLinkSyncConfigItemV4[] {
  return rows.map((row) => ({
    interval: row.interval,
    frequency: row.frequency,
    data_model: row.dataModel,
    category: row.category,
    scope_access: row.scopeAccess,
    enabled: row.enabled,
    sync_from: row.syncFrom ?? undefined,
  }));
}
